// Static imports of every subject animation, keyed by slug. Only the DATA is
// eagerly bundled (~11KB each) — the heavy part (lottie-react + the render loop)
// is still deferred via React.lazy and only mounts while a square is active, so
// untouched squares still do no work.
import advancedLeadership from '../../assets/animations/subjects/advanced-leadership.json'
import aiForLeaders from '../../assets/animations/subjects/ai-for-leaders.json'
import aiFoundations from '../../assets/animations/subjects/ai-foundations.json'
import careerPower from '../../assets/animations/subjects/career-power.json'
import financialAndDataIntelligence from '../../assets/animations/subjects/financial-and-data-intelligence.json'
import highOutputThinking from '../../assets/animations/subjects/high-output-thinking.json'
import marketing from '../../assets/animations/subjects/marketing.json'
import powerAndInfluence from '../../assets/animations/subjects/power-and-influence.json'
import productInnovation from '../../assets/animations/subjects/product-innovation.json'
import strategyAndOperations from '../../assets/animations/subjects/strategy-and-operations.json'
import { canonicalSubjectThemeKey } from './subjectThemeAliases'

// Subjects that ship a Lottie square animation. Each entry matches the live
// subject `slug` from the homepage API exactly, and a file of the same name
// lives at ../../assets/animations/subjects/<slug>.json.
export const ANIMATED_SUBJECT_SLUGS = [
  'advanced-leadership',
  'ai-for-leaders',
  'ai-foundations',
  'career-power',
  'financial-and-data-intelligence',
  'high-output-thinking',
  'marketing',
  'power-and-influence',
  'product-innovation',
  'strategy-and-operations',
] as const

// Non-subject animated square that reuses the exact same Lottie motion, only
// recolored to the brand palette — powers the generic loader. Computed at
// runtime from the `marketing` animation (see `recolor` below) rather than
// shipped as a separate JSON file.
export const NON_SUBJECT_ANIMATION_SLUGS = ['brand'] as const

// Lottie JSON is a deeply-nested document; we only hand it straight to the
// renderer, so an opaque object type is enough (and avoids an `any`).
export type LottieAnimationData = Record<string, unknown>

// ── Runtime recolor (brand) ──────────────────────────────────────────────────

const toRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
]
const toHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`

// marketing's source colors → the brand palette. The visible squares come from
// this animation's per-layer color EFFECTS (recolored alongside the fills, see
// `recolor`). The generic loader reads outer blue / mid green / inner yellow
// using only brand tokens:
//   edu-brand-blue #3e51af · edu-brand-green #129223 · edu-brand-yellow #fecb02
// marketing's own blue (#3e51af) already IS edu-brand-blue; its second, off-brand
// navy layer (and the never-rendered light-blue accent) fold into brand blue so
// the two full-size background squares share one color (no seam); and its
// green/yellow swap so the mid ring lands on the brand green.
const BRAND_MAP: Record<string, string> = {
  '#1e2391': '#3e51af', // off-brand navy bg layer → brand blue
  '#3e51af': '#3e51af', // brand blue → keep (edu-brand-blue)
  '#4897d3': '#3e51af', // never-rendered light-blue accent → brand blue
  '#489955': '#fecb02', // green → brand yellow (inner square)
  '#fecb02': '#129223', // yellow → brand green (mid ring)
  '#ff7504': '#129223', // never-rendered orange accent → green
  '#ffaa0a': '#fecb02', // never-rendered amber accent → yellow
}
type ColorMap = (r: number, g: number, b: number) => [number, number, number]
const brandColor: ColorMap = (r, g, b) => {
  const target = BRAND_MAP[toHex(r, g, b)]
  return target ? toRgb(target) : [r, g, b]
}

const isColorArr = (a: unknown): a is number[] =>
  Array.isArray(a) &&
  a.length >= 3 &&
  a.length <= 4 &&
  a.every((x) => typeof x === 'number' && x >= 0 && x <= 1)

// Deep-clone a subject animation and remap every fill/stroke color (static and
// keyframed) through `map`, preserving the motion exactly.
const recolor = (
  source: LottieAnimationData,
  map: ColorMap,
): LottieAnimationData => {
  const clone = JSON.parse(JSON.stringify(source)) as LottieAnimationData
  const apply = (arr: number[]) => {
    const [nr, ng, nb] = map(
      Math.round(arr[0] * 255),
      Math.round(arr[1] * 255),
      Math.round(arr[2] * 255),
    )
    arr[0] = nr / 255
    arr[1] = ng / 255
    arr[2] = nb / 255
  }
  // Remap a color holder `{ k }` that is either a static color array or an
  // array of keyframes each carrying an `.s` color.
  const applyHolder = (holder: unknown) => {
    const k = (holder as { k?: unknown } | undefined)?.k
    if (isColorArr(k)) apply(k)
    else if (Array.isArray(k))
      k.forEach((kf) => {
        const s = (kf as { s?: unknown } | null)?.s
        if (isColorArr(s)) apply(s)
      })
  }
  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (node && typeof node === 'object') {
      const rec = node as Record<string, unknown>
      // Solid fills/strokes carry their color on `c`.
      if (rec.ty === 'fl' || rec.ty === 'st') applyHolder(rec.c)
      // Color effect parameters (`ty: 2`) carry it on `v` — these drive the
      // visible squares in these animations, so they must be recolored too.
      if (rec.ty === 2) applyHolder(rec.v)
      Object.values(rec).forEach(walk)
    }
  }
  walk(clone)
  return clone
}

const marketingData = marketing as LottieAnimationData
const brandData = recolor(marketingData, brandColor)

export const ANIMATIONS: Readonly<Record<string, LottieAnimationData>> = {
  'advanced-leadership': advancedLeadership,
  'ai-for-leaders': aiForLeaders,
  'ai-foundations': aiFoundations,
  'career-power': careerPower,
  'financial-and-data-intelligence': financialAndDataIntelligence,
  'high-output-thinking': highOutputThinking,
  marketing: marketingData,
  'power-and-influence': powerAndInfluence,
  'product-innovation': productInnovation,
  'strategy-and-operations': strategyAndOperations,
  brand: brandData,
}

/** The animation key a slug plays (resolving theme aliases such as C2 subject
 *  keys), or `undefined` to keep the static brand mark. */
export const resolveSubjectAnimation = (slug: string): string | undefined => {
  const key = canonicalSubjectThemeKey(slug)
  return Object.hasOwn(ANIMATIONS, key) ? key : undefined
}

/** Resolve a slug's Lottie data (async to keep the activate-then-load latch in
 *  SubjectAnimation), or `undefined` if it has no animation. */
export const loadSubjectAnimation = (
  slug: string,
): Promise<LottieAnimationData> | undefined => {
  const key = resolveSubjectAnimation(slug)
  return key ? Promise.resolve(ANIMATIONS[key]) : undefined
}

// A few exports carry a brief idle lead-in before the motion starts, so they
// appear to "begin late" next to the others. Loop only the moving portion
// ([startFrame, endFrame]) so every square starts animating immediately and in
// sync. Omit a subject to play its full timeline.
const SUBJECT_ANIMATION_SEGMENTS: Record<string, [number, number]> = {
  // `marketing` holds for ~11 frames (~0.37s) before moving; the rest start
  // ~instantly. `brand` is recolored marketing, so it shares the segment.
  marketing: [11, 194],
  brand: [11, 194],
}

/** The [start, end] frame segment to loop for a subject's animation, or
 *  `undefined` to play the full timeline. */
export const resolveSubjectAnimationSegment = (
  slug: string,
): [number, number] | undefined => {
  const key = resolveSubjectAnimation(slug)
  return key ? SUBJECT_ANIMATION_SEGMENTS[key] : undefined
}
